import { Link } from "react-router";
import { FlaskConical, Sliders } from "lucide-react";
import { blockImageUrl } from "./api";
import { accentFor, type BlockSummary } from "./types";

/**
 * One block in the gallery.
 *
 * The artwork bleeds to the card edges and the accent hue is pulled from the
 * same palette the image was rendered in, so the card reads as one object
 * rather than a picture sitting in a box.
 */
export function BlockCard({ block }: { block: BlockSummary }) {
  const accent = accentFor(block.category);

  return (
    <Link
      to={`/blocks/${block.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-ink-700 bg-ink-900 transition-all duration-200 hover:-translate-y-0.5 hover:border-ink-400"
      style={{ ["--accent" as string]: accent }}
    >
      {/* Accent hairline that lights up on hover — a cheap way to tie the card
          to its category without adding another coloured chip. */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-px opacity-40 transition-opacity group-hover:opacity-100"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
      />

      {block.has_image && (
        <div className="aspect-[16/10] overflow-hidden bg-ink-950">
          <img
            src={blockImageUrl(block.slug)}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </div>
      )}

      <div className="flex flex-1 flex-col gap-2.5 p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-medium tracking-tight text-ink-50">{block.name}</h3>
          <span className="shrink-0 font-mono text-[0.6875rem] text-ink-400">
            v{block.version}
          </span>
        </div>

        <p className="flex-1 text-sm leading-relaxed text-ink-400">{block.tagline}</p>

        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span
            className="rounded-full px-2 py-0.5 text-[0.6875rem] font-medium"
            style={{ background: `color-mix(in oklab, ${accent} 16%, transparent)`, color: accent }}
          >
            {block.category}
          </span>
          {block.has_fake && <Badge icon={<FlaskConical size={11} />}>fake included</Badge>}
          {/* "works out of the box" is the single most useful thing to know
              before choosing between two blocks, so it earns a badge. */}
          {!block.needs_configuration && <Badge>no config</Badge>}
          {block.needs_configuration && (
            <Badge icon={<Sliders size={11} />}>needs config</Badge>
          )}
        </div>
      </div>
    </Link>
  );
}

function Badge({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1 rounded-full border border-ink-700 px-2 py-0.5 text-[0.6875rem] text-ink-400">
      {icon}
      {children}
    </span>
  );
}
